import { isTerminalQueueItemStatus, type LatestQueueItem, TERMINAL_OPT_JOB_STATUSES } from 'api/optJobs/optJobs.api'

export function queueItemIsDone(item: LatestQueueItem): boolean {
  // Gating policy, not a claim about the item's lifecycle: a terminal opt job no longer gates
  // starting the next one, so none of its queue items do either — including rows a dead
  // optimization worker left non-terminal, which nothing reconciles. Because a single failure
  // makes the whole job terminal, siblings can still be solving and can still write their own
  // terminal status afterwards; not waiting for them is deliberate.
  if (TERMINAL_OPT_JOB_STATUSES.has(item.opt_job_status)) {
    return true
  }

  // A row reporting no status at all is not evidence of work in flight. Preserves the
  // treatment this had before the opt job status was consulted.
  const status = item.queue_item_status
  return !status || isTerminalQueueItemStatus(status)
}

export function optimizationIsRunning(latestQueueItems: LatestQueueItem[]): boolean {
  return latestQueueItems.some((item) => !queueItemIsDone(item))
}

/**
 * The instant an optimization last completed in full, or null if none has.
 *
 * Stricter than the tooltip's notion of "latest finished", deliberately. A fan-out
 * subtype can reach a 'Finished' queue item status while its parent job is still
 * 'Running' and its siblings are still solving — `queueItemIsDone` above exists
 * because that state is real. Showing a user the most recent settled subtype is
 * reasonable; publishing it as a completion time is not, because an external suite
 * asserting on it would report a finished optimization mid-run. So the parent job
 * must have finished too.
 */
export function lastCompletedAt(latestQueueItems: LatestQueueItem[]): string | null {
  const completed = latestQueueItems.filter(
    (item) => item.opt_job_status === 'Finished' && item.queue_item_status === 'Finished'
  )
  if (completed.length === 0) return null

  return completed.reduce((latest, current) =>
    new Date(current.updated_at) > new Date(latest.updated_at) ? current : latest
  ).updated_at
}
